/* eslint-disable import/no-named-as-default-member */
import React from 'react';
import PropTypes from 'prop-types';
import { TrackingProvider, TrackingContext } from '@vrbo/react-event-tracking';
import { instance } from 'eg-clickstream-sdk-js';
import edap from './EdapUtil';
import { egClickstreamMap } from './EgClickstreamUtil';

const pageLoad = (data) => {
    const { pageName, ...payload } = data;
    edap.pageLoad(pageName, payload);
};

const edapMap = {
    pageUnload: edap.pageUnload,
    pageLoad,
    genericClick: edap.genericClick,
    genericEvent: edap.genericEvent,
    userAccountCreated: edap.userAccountCreated,
    listingOnboardingStarted: edap.listingOnboardingStarted,
    testExposure: edap.testExposure
};

const egClickstreamInstance = instance.getNewInstance();

export const EdapEventProvider = (props) => {
    const {
        eventPayload,
        eventOptions,
        payload,
        children,
        options,
        overwrite
    } = props;

    const trigger = (name, triggerPayload = {}, triggerOptions = {}) => {
        if (edapMap[name]) {
            edapMap[name](triggerPayload);
        }
        return { name, triggerPayload, triggerOptions };
    };

    // Wrapping the TrackingProvider in a Consumer so that a trigger function
    // specified higher in the DOM chain will be used instead of the one defined
    // by this class. The main use case for this is to allow a future test provider
    // to override the trigger of children for event validation.

    return (
        <TrackingContext.Consumer>
            {() => {
                const innerTrigger = (name, triggerPayload, triggerOptions) => {
                    const clickstreamMap = egClickstreamMap;
                    trigger(name, triggerPayload, triggerOptions);
                    /* istanbul ignore next */
                    if (clickstreamMap[name]) {
                        egClickstreamInstance.publish(triggerPayload);
                    }
                };
                return (
                    <TrackingProvider
                        eventPayload={eventPayload}
                        eventOptions={eventOptions}
                        payload={payload}
                        options={options}
                        overwrite={overwrite}
                        trigger={innerTrigger}
                    >
                        {children}
                    </TrackingProvider>
                );
            }}
        </TrackingContext.Consumer>
    );
};
EdapEventProvider.propTypes = {
    /** An object of event specific fields where the event name is the key and
 * the value is an object of field key/value pairs for the event. Event
 * specific values will be merged with defaults from the `payload` property. */
    eventPayload: PropTypes.objectOf(PropTypes.objectOf(PropTypes.string)),
    /** An object of event specific options where the event name is the key and
     * the value is an object of option key/value pairs for the event. Event
     * specific values will be merged with defaults from the `options` property. */
    eventOptions: PropTypes.objectOf(PropTypes.objectOf(PropTypes.string)),
    /** Object of string values that represents the default fields to apply to
     * all events within this context. */
    payload: PropTypes.objectOf(PropTypes.string),
    /** The trigger options. */
    options: PropTypes.objectOf(PropTypes.string),
    /** When true, overwrites the current context with specified properties.
     * Default is to merge instead of overwrite. */
    overwrite: PropTypes.bool,
    children: PropTypes.node.isRequired
};

EdapEventProvider.defaultProps = {
    eventPayload: undefined,
    eventOptions: undefined,
    payload: undefined,
    options: undefined,
    overwrite: false
};

export default {
    EdapEventProvider
};
